/**
 * Отчёт «Головне» — итоги периода для клиента и супервайзера (#831).
 * Спека: docs/plans/2026-09-17-highlights-tab.md
 */
import { z } from 'zod/v4';
import { analyticsPeriodSchema } from './analytics';
import { projectIdsFilterSchema } from './common';

export const REPORT_TOP_ISSUES_LIMIT = 3;
/**
 * Самое длинное окно отчётов `/reports/*` в сутках — квартал с запасом на
 * перевод часов (почему 93 — `resolveReportWindow` в edge-api). Карточка
 * просадок сотрудника (#767) на окне длиннее не спрашивает, а говорит об этом.
 */
export const REPORT_MAX_WINDOW_DAYS = 93;

/**
 * Окно отчёта длиннее предела — одно правило на сервер (`resolveReportWindow`)
 * и на карточку просадок, которая такое окно не спрашивает. Обе границы
 * включены, как `periodStart`/`periodEnd` запроса.
 */
export function reportWindowTooLong(start: Date, endInclusive: Date): boolean {
  return endInclusive.getTime() - start.getTime() > REPORT_MAX_WINDOW_DAYS * 86_400_000;
}
export const REPORT_ISSUE_EXAMPLES_PER_KEY = 2;
export const REPORT_FLAGGED_LIST_LIMIT = 50;
export const REPORT_PRODUCT_ISSUES_LIMIT = 20;
export const REPORT_COMPETITORS_LIMIT = 10;
/**
 * У всех соседних списков voice-блока (жалобы, конкуренты) есть и потолок в
 * схеме, и `LIMIT` в SQL — у категорий разговоров до этой правки не было ни
 * того, ни другого (issue #831, ревью раунда 2, находка B). Значение то же,
 * что у жалоб: категорий у одной организации не бывает больше, чем видов
 * обратной связи, ориентир общий.
 */
export const REPORT_CATEGORIES_LIMIT = 20;
/** Оператор с одним звонком не должен становиться «лучшим» или «отстающим». */
export const REPORT_AGENT_MIN_SCORED_CALLS = 5;

export const reportSummaryQuerySchema = z.object({
  period: analyticsPeriodSchema,
  periodStart: z.string().datetime({ offset: true }).optional(),
  periodEnd: z.string().datetime({ offset: true }).optional(),
  // Не .uuid(): id проектов демо-организации — не UUID (как outcomeFunnelQuerySchema).
  /** @deprecated legacy single-project filter — see callStatsQuerySchema's note. */
  projectId: z.string().optional(),
  projectIds: projectIdsFilterSchema,
  /**
   * Без `.default('yes')`, в отличие от соседних схем (callStatsQuerySchema и
   * т.п.): маршрут раньше жёстко ставил «без демо» для всех запросов
   * (issue #831, финальное ревью, находка I3), и отсутствие параметра должно
   * сохранять именно это прежнее поведение, а не молча переключиться на
   * «включая демо». UI (`useDemoFilterFromAuth`) всегда шлёт значение явно.
   */
  includeDemo: z.enum(['yes', 'no']).optional(),
});

/**
 * Продолжение «Потребують уваги» (#1095): те же параметры, что у сводки, и
 * последняя показанная строка. Keyset, а не offset — отмеченный разговор,
 * появившийся между страницами, сдвинул бы offset в повтор.
 */
export const reportFlaggedQuerySchema = reportSummaryQuerySchema.extend({
  // Обязательны: относительный период пересчитывает «сейчас» на каждой
  // порции, и страницы листали бы разные окна.
  periodStart: z.string().datetime({ offset: true }),
  periodEnd: z.string().datetime({ offset: true }),
  afterSeverity: z.enum(['high', 'critical']),
  afterCreatedAt: z.string().datetime({ offset: true }),
  afterRecordId: z.string().min(1).max(64),
});

/**
 * Корзины в порядке показа. Порядок ПРОВЕРКИ — в `classifyFunnelRow` (@aica/database).
 *
 * `missed` — пропущенный звонок (`call_category = 'missed'`, #1337): до него
 * такие звонки падали в «без записи разговора» вместе с настоящими пропажами
 * записи.
 */
export const REPORT_FUNNEL_BUCKETS = [
  'analyzed',
  'missed',
  'awaiting_start',
  'too_short',
  'no_recording',
  'in_progress',
  'failed',
] as const;
export const reportFunnelBucketSchema = z.enum(REPORT_FUNNEL_BUCKETS);

const countSchema = z.number().int().nonnegative();

/**
 * Собрано из REPORT_FUNNEL_BUCKETS одним выражением, а не перечислено
 * заново: раньше массив и этот объект были двумя независимыми литералами
 * одних и тех же шести имён, и седьмая корзина, добавленная только в один из
 * них, либо молча срезалась бы Zod (только в массиве — emptyBuckets()
 * создаёт лишний ключ), либо роняла бы весь контракт как missing field
 * (только здесь) — issue #831, финальное ревью, находка 5.
 */
const reportFunnelBucketsSchema = z.object({
  ...(Object.fromEntries(
    REPORT_FUNNEL_BUCKETS.map((bucket) => [bucket, countSchema])
  ) as Record<
    Exclude<(typeof REPORT_FUNNEL_BUCKETS)[number], 'missed'>,
    typeof countSchema
  >),
  /**
   * Необязательна только для чтения: ответ, закэшированный до корзины
   * (`/reports/summary` — пять минут плюс stale-while-revalidate), должен
   * рисовать вкладку — его пропущенные лежат в `no_recording`, и строки
   * «Пропущенные» у него просто нет. Сервер отдаёт её всегда.
   */
  missed: countSchema.optional(),
});

export const reportFunnelChannelSchema = z.object({
  /** `records.type`: сегодня `call` или `chat`; строка, а не enum, — новый канал не должен ронять вкладку. */
  channel: z.string(),
  total: countSchema,
  buckets: reportFunnelBucketsSchema,
  /**
   * Записи, не попавшие ни в одну корзину. Ненулевое значение — один из двух
   * случаев, которые `classifyFunnelRow` (@aica/database) намеренно не
   * относит ни к одной корзине:
   *  - статус без определённой корзины: либо намеренное исключение (например
   *    `processing_objected`), либо недосмотр вроде `not_applicable` до issue
   *    #831 финального ревью (находка 1);
   *  - терминальная запись (успех или `not_applicable`) с транскриптом, но БЕЗ
   *    текущего анализа и НЕ короткая — поступила, не анализировалась, причина
   *    неизвестна, честной корзины для неё нет (issue #831, ревью раунда 2,
   *    находка F).
   * В любом случае это повод расследовать, а не штатный ноль — статус не
   * обязательно новый, он мог существовать всегда.
   */
  unclassified: countSchema,
});

export const reportTopIssueSchema = z.object({
  /** Канонический ключ (`normalizeCriterionKey`); название на языке интерфейса подставляет UI. */
  criterionKey: z.string(),
  calls: countSchema,
  examples: z
    .array(z.object({ recordId: z.string(), quote: z.string().nullable() }))
    .max(REPORT_ISSUE_EXAMPLES_PER_KEY),
});

export const reportFlaggedCallSchema = z.object({
  recordId: z.string(),
  createdAt: z.string(),
  agentName: z.string().nullable(),
  severity: z.enum(['high', 'critical']),
  /**
   * The model's reason, in its words. Absent for a narrowed access grant
   * without `evidence.text` (E1.5, #1336); the list shows «no reason», as for
   * an empty one.
   */
  reason: z.string().optional(),
});

export const reportAgentScoreSchema = z.object({
  agentId: z.string(),
  /**
   * Установка CRM сотрудника без аккаунта Aelo (его id локален для портала),
   * иначе `null` (#1927): один несвязанный id в двух порталах — два человека, и
   * различает их это поле. Необязательное — страница переживает откат edge-api
   * на релиз без него.
   */
  installationId: z.string().nullable().optional(),
  agentName: z.string().nullable(),
  avgScore: z.number(),
  scoredCalls: countSchema,
});

export const reportCategorySchema = z.object({
  category: z.string(),
  calls: countSchema,
});

export const reportProductIssueSchema = z.object({
  recordId: z.string(),
  product: z.string(),
  feedback: z.string(),
  severity: z.enum(['incident', 'major_issue']),
});

/**
 * Конкурент в сводке (#842): имя каноническое — `term` глоссария клиента,
 * иначе написание, названное в наибольшем числе разговоров. Единица счёта —
 * разговор, поэтому два написания в одном звонке дают одну единицу.
 *
 * `merged: false` — имени нет в глоссарии. Без этого признака совет «пришлите
 * недостающие написания в поддержку» невыполним: доля названа, а какие именно
 * строки её составляют — нет (код-ревью, находка 10).
 *
 * Слово одно на весь контракт — «сведено»: `merged` у строки,
 * `competitorsUnmergedShare` и `competitorsUnmergedExamples` рядом с ней.
 * Внутренний примитив `@aica/core/voc` говорит `mapped`, потому что общий с
 * кодами возражений; наружу это не протекает (код-ревью 2, находка 15).
 */
export const reportCompetitorSchema = z.object({
  name: z.string(),
  calls: countSchema,
  merged: z.boolean(),
});

/** Счёт встроенного по одному источнику: записи с результатом и сработавшие. */
export const reportBuiltinCheckCountsSchema = z.object({
  /** Записи окна с результатом: применимо и не «не уверен». */
  evaluated: countSchema,
  /** Из них — встроенный сработал. */
  triggered: countSchema,
});

/**
 * Встроенный критерий в блоке «Головне» (Э2, #2517; план
 * `docs/plans/2026-10-18-builtin-criteria-e2.md`, решение 7). Счёт делится по
 * источнику результата записи (порядок a–e читателя Э2):
 * - `answers` — ответы критерия; за то же окно совпадают со строкой критерия
 *   в отчёте «Критерии» (`criterionId` — ссылка туда), пока `truncated` ложно;
 * - `analysis` — старое поле анализа разговора: записи до первых отметок
 *   прогона (09.10), пропущенный или упавший шаг критериев, реплей. В отчёте
 *   «Критерии» этих записей нет — ссылки у части нет.
 */
export const reportBuiltinCheckSchema = z.object({
  /**
   * Ключ каталога встроенных — строкой, не перечислением: новый встроенный
   * приходит с edge-api раньше UI, и перечисление уронило бы всю вкладку
   * (как `channel` и `criterionKey` выше).
   */
  key: z.string(),
  criterionId: z.string(),
  /** Имя критерия, как его видит клиент в настройке критериев. */
  name: z.string(),
  answers: reportBuiltinCheckCountsSchema,
  analysis: reportBuiltinCheckCountsSchema,
});

export const reportBuiltinChecksSchema = z.object({
  checks: z.array(reportBuiltinCheckSchema),
  /** Просмотренные записи окна — до потолка читателя. */
  scannedCalls: countSchema,
  /** Записей в окне больше потолка: счёт — по самым новым, равенства с отчётом нет. */
  truncated: z.boolean(),
});

export const reportSummaryResponseSchema = z.object({
  periodStart: z.string(),
  periodEnd: z.string(),
  /**
   * The projects the report counted: the chosen ones (narrowed to those the
   * caller may see) or, for a supervisor who chose none, their own — the very
   * list the Postgres queries were filtered by (`memberOf`, #833); null — the
   * whole organization. A funnel row's link names exactly these (#1233). A
   * group lead (E1.5) gets the chosen projects or null too: its people are not
   * named here, the records list holds it to the same people itself.
   *
   * Optional: a response cached before the field existed (five minutes plus
   * stale-while-revalidate) must still render the tab — its rows just stay
   * plain numbers (Codex on #1282).
   */
  projectIds: z.array(z.string()).nullable().optional(),
  funnel: z.object({
    total: countSchema,
    channels: z.array(reportFunnelChannelSchema),
  }),
  headline: z.object({
    analyzed: countSchema,
    topIssues: z.array(reportTopIssueSchema).max(REPORT_TOP_ISSUES_LIMIT),
    /**
     * Замечания без известного ключа: и с ключом вне рубрики
     * (normalizeCriterionKey вернул null для непустой строки), и вовсе без
     * criterionKey (IssueKeyRow с `criterionKey: null`) — оба случая суммируются
     * в одно число, «вне рубрики» в имени поля покрывает не всё (issue #831,
     * финальное ревью, находка 7). В топ не входят, но их число видно.
     *
     * Единица измерения — УПОМИНАНИЯ (`rankIssueKeys` суммирует
     * `IssueKeyRow.mentions`), а не разговоры: одна запись может дать
     * несколько замечаний вне рубрики и даст несколько единиц здесь. У каждой
     * проблемы в `topIssues` единица — `calls` (разговоры, `COUNT(DISTINCT
     * records.id)`). Числа рядом в одном ответе, но в разных единицах —
     * складывать их со `calls` соседних проблем нельзя (issue #831, ревью
     * раунда 2, находка G).
     */
    unrecognizedIssues: countSchema,
    /**
     * Проанализированные без пометки внимания high ИЛИ critical — несмотря на
     * имя, вычитаются обе серьёзности (`FLAGGED` в ReportRepository.ts: `triggered
     * = true AND severity IN ('high', 'critical')`), не только critical
     * (issue #831, финальное ревью, находка 7).
     */
    withoutCritical: countSchema,
    flaggedTotal: countSchema,
    /** Сначала critical, затем новые; не больше REPORT_FLAGGED_LIST_LIMIT. */
    flaggedCalls: z.array(reportFlaggedCallSchema).max(REPORT_FLAGGED_LIST_LIMIT),
    /**
     * Есть ли отмеченные сверх `flaggedCalls` — по строке сверх порции (#1095).
     * Необязательное: интерфейс, выкаченный раньше API, не должен ронять всю
     * сводку из-за отсутствующего поля; без него судит по `flaggedTotal`.
     */
    flaggedHasMore: z.boolean().optional(),
    /**
     * Отпечаток состава отмеченных — какие записи и с какой серьёзностью
     * (#1239). Итоги сводки могут совпасть при другом составе (−1 флаг выше,
     * +1 ниже конца догруженного), и закешированное «дальше нет» прятало бы
     * новую строку; отпечаток меняется ровно тогда, когда состав сменился.
     * Необязательное по той же причине, что `flaggedHasMore`.
     */
    flaggedFingerprint: z.string().optional(),
  }),
  quality: z.object({
    avgScore: z.number().nullable(),
    /** Изменение к прошлому периоду той же длины, % — тот же расчёт, что у карточки качества. */
    trendPercent: z.number().nullable(),
    scoredCalls: countSchema,
    unscoredCalls: countSchema,
    bestAgent: reportAgentScoreSchema.nullable(),
    worstAgent: reportAgentScoreSchema.nullable(),
  }),
  voice: z.object({
    categories: z.array(reportCategorySchema).max(REPORT_CATEGORIES_LIMIT),
    productIssues: z.array(reportProductIssueSchema).max(REPORT_PRODUCT_ISSUES_LIMIT),
    competitors: z.array(reportCompetitorSchema).max(REPORT_COMPETITORS_LIMIT),
    /**
     * Доля пар (разговор, конкурент), чьё имя не нашлось в глоссарии (0-1).
     * Показывается, а не прячется: это мера полноты глоссария клиента.
     * Ноль, когда глоссария нет: пополнять нечего, упрекать не за что.
     *
     * Поле обязательное, без `default`: отсутствие — поломка сервера, а не
     * «несведённых нет». `default(0)` жил один релиз, пока в кеше браузера
     * могли лежать ответы до v2.43.0 (#936).
     */
    competitorsUnmergedShare: z.number().min(0).max(1),
    /**
     * До трёх несведённых имён, самые частые первыми. Доля без них — совет
     * без адреса: несведённое имя редко бывает частым, поэтому в показанной
     * десятке его обычно нет, и клиенту нечего слать в поддержку (код-ревью
     * 2, находка 3).
     */
    competitorsUnmergedExamples: z.array(z.string()).max(3),
    /**
     * Выборка упёрлась в потолок сканирования: счётчики и доля посчитаны по
     * части периода. Флаг есть в контракте, потому что иначе неполнота
     * молчалива — у соседней карточки возражений `truncated` ровно за этим и
     * живёт (код-ревью, находка 2).
     */
    competitorsTruncated: z.boolean(),
    /**
     * Глоссарий организации есть, но прочитать его не удалось — имена сейчас
     * не сводятся, и счётчики ниже разбиты по написаниям. Без этого флага
     * авария выглядела бы как «у вас всё сведено»: доля нулевая, пометок нет,
     * а ответ живёт в кеше браузера до часа (код-ревью 2, находка 2).
     */
    competitorsMergeUnavailable: z.boolean(),
  }),
  /**
   * Сработавшие встроенные критерии (Э2, #2517) — блок рядом с топом проблем,
   * метрика топа не меняется. Только у организации с Э2 (действующее значение
   * `builtinCriteriaVisible`); окно и область записей — те же, что у сводки.
   * Необязательное: без поля блока нет, как до Э2 (откат edge-api при
   * выкаченном UI безопасен).
   */
  builtinChecks: reportBuiltinChecksSchema.optional(),
});

/**
 * What the client may receive (E1.5, #1336): the route's summary, where a
 * narrowed access grant gets only the sections of the aggregates it covers —
 * `funnel` needs `aggregates.flow`, `headline` and `quality` need
 * `aggregates.quality`, `voice` needs `aggregates.voc`; the others are absent
 * and the tab renders the sections it got. Every role still gets all four:
 * the route builds `reportSummaryResponseSchema`, the client parses this.
 */
export const reportSummaryClientResponseSchema = reportSummaryResponseSchema.partial({
  funnel: true,
  headline: true,
  quality: true,
  voice: true,
});

/**
 * Ключ строки «Команды» (`DashboardTeamEmployee.key`): id Aelo,
 * `<id установки>:<id>` или голый `agent_id` (до 255 символов) с префиксом
 * установки.
 */
export const EMPLOYEE_ISSUES_KEY_MAX = 320;

/**
 * Просадки сотрудника для руководителя (#767): параметры «Главного» и ключ
 * строки «Команды». Одна область с «Главным» — одно число с его топ-проблемами.
 */
export const employeeIssuesQuerySchema = reportSummaryQuerySchema.extend({
  employee: z.string().min(1).max(EMPLOYEE_ISSUES_KEY_MAX),
});

/**
 * Три самых частых просадки сотрудника за окно (#767) — `headline.topIssues`
 * и `headline.analyzed` «Главного», суженного до записей сотрудника. Одна
 * схема у «Команды» (`/reports/employee-issues`) и «Моё» (`/agents/me/issues`).
 */
export const employeeIssuesResponseSchema = z.object({
  periodStart: z.string(),
  periodEnd: z.string(),
  projectIds: z.array(z.string()).nullable(),
  analyzed: countSchema,
  topIssues: z.array(reportTopIssueSchema).max(REPORT_TOP_ISSUES_LIMIT),
});

/**
 * Блок «После фидбека» (#2609, спека `docs/plans/2026-10-25-after-feedback-block.md`):
 * сколько разговоров с баллом по критерию берётся до даты последнего фидбека
 * руководителя и после неё (Т2), и с какого числа в каждом окне сравнение
 * показывается (Т3). Одно правило на сервер и на карточку.
 */
export const FEEDBACK_EFFECT_WINDOW_CALLS = 10;
export const FEEDBACK_EFFECT_MIN_CALLS = 3;

/**
 * Критерии рубрики с баллом звонка — те же ключи, что `SUB_METRIC_CRITERION_KEYS`
 * (`@aica/core`), в том же порядке. Здесь — литералом: `api-types` не зависит
 * от `core`; сверку держит `criterion-keys.test.ts` в `core`.
 */
export const FEEDBACK_EFFECT_CRITERION_KEYS = [
  'greeting_intro',
  'needs_qualification',
  'clarity',
  'objection_handling',
  'closing_techniques',
  'politeness',
] as const;

/** Параметры блока: проекты и демо, как у «Главного», — окна задаёт фидбек, не период. */
const feedbackEffectFilterSchema = reportSummaryQuerySchema.pick({
  projectId: true,
  projectIds: true,
  includeDemo: true,
});

/** «Моё» — `GET /agents/me/feedback-effect`. */
export const myFeedbackEffectQuerySchema = feedbackEffectFilterSchema;

/** «Команда» — `GET /reports/employee-feedback-effect`, ключ строки «Команды». */
export const employeeFeedbackEffectQuerySchema = feedbackEffectFilterSchema.extend({
  employee: z.string().min(1).max(EMPLOYEE_ISSUES_KEY_MAX),
});

const feedbackEffectWindowSchema = z.object({
  /** Разговоров с баллом по критерию в окне (≤ `FEEDBACK_EFFECT_WINDOW_CALLS`). */
  calls: countSchema.max(FEEDBACK_EFFECT_WINDOW_CALLS),
  /**
   * Средний балл, до 0,1; `null` — разговоров нет. Обычно 0–100, но без
   * границ, как `avgScore` агентов: подметрика из ответа модели хранится как
   * есть, и одно значение вне шкалы не должно ронять ответ целиком.
   */
  avgScore: z.number().nullable(),
});

export const feedbackEffectCriterionSchema = z.object({
  criterionKey: z.enum(FEEDBACK_EFFECT_CRITERION_KEYS),
  before: feedbackEffectWindowSchema,
  after: feedbackEffectWindowSchema,
});

/**
 * Балл сотрудника по критериям за N разговоров до и N после последнего
 * фидбека руководителя. `feedbackAt: null` — фидбека не было, `criteria`
 * пуст. Одна схема у «Команды» и «Моё»; ни текста фидбека, ни автора.
 */
export const feedbackEffectResponseSchema = z.object({
  projectIds: z.array(z.string()).nullable(),
  feedbackAt: z.string().datetime({ offset: true }).nullable(),
  criteria: z
    .array(feedbackEffectCriterionSchema)
    .max(FEEDBACK_EFFECT_CRITERION_KEYS.length),
});

/** Следующая порция «Потребують уваги» — тот же порядок, что у сводки (#1095). */
export const reportFlaggedResponseSchema = z.object({
  flaggedCalls: z.array(reportFlaggedCallSchema).max(REPORT_FLAGGED_LIST_LIMIT),
  /**
   * Есть ли строки после этой порции — по живой базе. Клиент не может судить
   * об этом по `flaggedTotal` сводки: сводка кешируется, и её число отстаёт
   * от того, что продолжение видит сейчас.
   */
  hasMore: z.boolean(),
});

export type ReportSummaryQuery = z.infer<typeof reportSummaryQuerySchema>;
export type EmployeeIssuesQuery = z.infer<typeof employeeIssuesQuerySchema>;
export type EmployeeIssuesResponse = z.infer<typeof employeeIssuesResponseSchema>;
export type EmployeeFeedbackEffectQuery = z.infer<
  typeof employeeFeedbackEffectQuerySchema
>;
export type FeedbackEffectCriterion = z.infer<typeof feedbackEffectCriterionSchema>;
export type FeedbackEffectResponse = z.infer<typeof feedbackEffectResponseSchema>;
export type ReportFlaggedQuery = z.infer<typeof reportFlaggedQuerySchema>;
export type ReportFlaggedResponse = z.infer<typeof reportFlaggedResponseSchema>;
export type ReportSummaryResponse = z.infer<typeof reportSummaryResponseSchema>;
export type ReportSummaryClientResponse = z.infer<
  typeof reportSummaryClientResponseSchema
>;
export type ReportFunnelBucket = z.infer<typeof reportFunnelBucketSchema>;
export type ReportFunnelChannel = z.infer<typeof reportFunnelChannelSchema>;
export type ReportTopIssue = z.infer<typeof reportTopIssueSchema>;
export type ReportBuiltinCheck = z.infer<typeof reportBuiltinCheckSchema>;
export type ReportBuiltinChecks = z.infer<typeof reportBuiltinChecksSchema>;
export type ReportFlaggedCall = z.infer<typeof reportFlaggedCallSchema>;
export type ReportAgentScore = z.infer<typeof reportAgentScoreSchema>;
export type ReportCategory = z.infer<typeof reportCategorySchema>;
export type ReportProductIssue = z.infer<typeof reportProductIssueSchema>;
export type ReportCompetitor = z.infer<typeof reportCompetitorSchema>;
